/* FR-JS guide hub. Local, explicit choices; no requests, persistence or tracking. */
export function calculateResult(data, answers, optional = {}) {
  const chooser = data.chooser;
  const selected = {};
  const missing = [];
  for (const question of chooser.questions) {
    const option = question.options.find((item) => item.id === answers[question.id]);
    if (!option) missing.push(question.id);
    else selected[question.id] = option;
  }
  if (missing.length) return { valid: false, missing };

  const matches = (rule) => rule.when.every((condition) => condition.in.includes(answers[condition.question]));
  const rule = chooser.primary_route_rules.rules.find(matches);
  if (!rule) return { valid: false, missing: [], error: 'route' };
  const guidance = Object.fromEntries(Object.entries(selected).map(([id, option]) => [id, chooser.guidance[option.guidance_id]]));
  const override = chooser.contextual_overrides.find(matches) || null;
  const checks = [{ id: 'hand', text: guidance.hand.check }];
  if (answers.training === 'awaiting_list') checks.push({ id: 'training', text: guidance.training.check });
  checks.push({ id: 'size', text: guidance.size.check }, { id: 'handle', text: guidance.handle.check });
  if (answers.training === 'list_ready') checks.push({ id: 'training', text: guidance.training.check });
  if (answers.training === 'not_training') checks.push({ id: 'role', text: guidance.role.action });

  const guideCandidates = [];
  if (answers.hand !== 'right') guideCandidates.push(guidance.hand.guide);
  guideCandidates.push(guidance.job.guide, guidance.size.guide, guidance.handle.guide);
  const used = new Set([rule.cta.href]);
  const guides = guideCandidates.filter((link) => {
    if (!link || used.has(link.href)) return false;
    used.add(link.href);
    return true;
  }).slice(0, 2);
  const optionalHandle = answers.hand === 'right' ? guidance.handle.optional_collection || null : null;
  const sourceIds = new Set(Object.values(guidance).map((block) => block.source_id).filter(Boolean));
  const sources = data.reference_notes.filter((source) => sourceIds.has(source.id));
  const details = Object.entries(guidance).filter(([, block]) => block.body).map(([id, block]) => ({ id, title: block.title, body: block.body }));
  const values = Object.fromEntries(Object.entries(selected).map(([id, option]) => [id, option.summary_label]));
  values.current_reference_or_non_renseignee = String(optional.current_reference || '').trim().slice(0, 300) || chooser.summary_labels.current_reference_empty;
  values.compared_references_or_non_renseignees = String(optional.compared_references || '').trim().slice(0, 1000) || chooser.summary_labels.compared_references_empty;
  values.checks = checks.map((check) => check.text).join(' / ');
  values.primary_route_url = data.site_origin + rule.cta.href;
  const summary = chooser.summary_template.replace(/\{([a-z_]+)\}/g, (token, key) => values[key] ?? token);
  return {
    valid: true,
    ruleId: rule.id,
    primary: rule,
    criteria: guidance.job.compare.slice(0, 2),
    checks,
    role: { title: guidance.role.title, action: guidance.role.action },
    override,
    details,
    guides,
    optionalHandle,
    sources,
    summary
  };
}

function element(document, tag, className, text) {
  const node = document.createElement(tag);
  if (className) node.className = className;
  if (text !== undefined) node.textContent = text;
  return node;
}

function appendLink(document, parent, link, className = 'frjs-guide-hub__text-link') {
  if (!link || !(/^\/(?!\/)/.test(link.href) || /^https:\/\//.test(link.href))) return;
  const anchor = element(document, 'a', className, link.label);
  anchor.setAttribute('href', link.href);
  parent.append(anchor);
}

function appendList(document, parent, items) {
  const list = element(document, 'ul', 'frjs-guide-hub__list');
  items.forEach((item) => list.append(element(document, 'li', '', item)));
  parent.append(list);
}

const chooserHashBindings = new WeakMap();

export function bindChooserHash(root, chooser, firstControl, view) {
  if (!view) return () => {};
  if (chooserHashBindings.has(root)) return chooserHashBindings.get(root);
  const onHash = () => {
    if (!root.isConnected || view.location.hash !== '#mon-choix') return;
    chooser.open = true;
    firstControl.focus({ preventScroll: true });
    chooser.scrollIntoView({ block: 'start' });
  };
  const cleanup = () => {
    view.removeEventListener('hashchange', onHash);
    chooserHashBindings.delete(root);
  };
  chooserHashBindings.set(root, cleanup);
  view.addEventListener('hashchange', onHash);
  onHash();
  return cleanup;
}

export function renderResult(root, data, result) {
  const document = root.ownerDocument;
  const container = root.querySelector('[data-frjs-result-body]');
  container.replaceChildren();
  const ui = data.chooser.ui;
  const primary = element(document, 'div', 'frjs-guide-hub__primary-result');
  primary.append(element(document, 'p', 'frjs-guide-hub__eyebrow', ui.primary_route_heading));
  primary.append(element(document, 'h4', '', result.primary.title));
  primary.append(element(document, 'p', '', result.primary.body));
  appendLink(document, primary, result.primary.cta, 'frjs-guide-hub__button');
  container.append(primary);

  const grid = element(document, 'div', 'frjs-guide-hub__result-grid');
  const criteria = element(document, 'div');
  criteria.append(element(document, 'h4', '', ui.criteria_heading));
  appendList(document, criteria, result.criteria);
  const checks = element(document, 'div');
  checks.append(element(document, 'h4', '', ui.priority_check_heading));
  appendList(document, checks, result.checks.map((check) => check.text));
  grid.append(criteria, checks);
  container.append(grid);

  const role = element(document, 'p', 'frjs-guide-hub__role');
  role.append(element(document, 'strong', '', result.role.title + ' '));
  role.append(document.createTextNode(result.role.action));
  container.append(role);
  if (result.override) {
    const note = element(document, 'div', 'frjs-guide-hub__note');
    note.append(element(document, 'strong', '', result.override.title));
    note.append(element(document, 'p', '', result.override.text));
    container.append(note);
  }
  const guideBox = element(document, 'div', 'frjs-guide-hub__related');
  guideBox.append(element(document, 'h4', '', ui.guides_heading));
  const guideLinks = element(document, 'ul', 'frjs-guide-hub__link-list');
  [...result.guides, result.optionalHandle].filter(Boolean).forEach((link) => {
    const li = element(document, 'li');
    appendLink(document, li, link);
    guideLinks.append(li);
  });
  guideBox.append(guideLinks);
  container.append(guideBox);

  const details = element(document, 'details', 'frjs-guide-hub__details');
  details.append(element(document, 'summary', '', 'Comprendre mes repères'));
  const detailBody = element(document, 'div', 'frjs-guide-hub__details-body');
  result.details.forEach((block) => {
    detailBody.append(element(document, 'h4', '', block.title));
    detailBody.append(element(document, 'p', '', block.body));
  });
  if (result.sources.length) {
    detailBody.append(element(document, 'h4', '', 'Des références pour aller plus loin'));
    result.sources.forEach((source) => {
      detailBody.append(element(document, 'p', '', source.body));
      const p = element(document, 'p');
      appendLink(document, p, source.link);
      detailBody.append(p);
    });
  }
  details.append(detailBody);
  container.append(details);
  root.querySelector('[data-frjs-summary]').value = result.summary;
}

export function initGuideHubs(scope) {
  const document = scope.ownerDocument || scope;
  const roots = [];
  if (scope.matches?.('[data-frjs-hub]')) roots.push(scope);
  roots.push(...scope.querySelectorAll('[data-frjs-hub]'));
  roots.forEach((root) => {
    if (root.dataset.frjsReady) return;
    try {
      const data = JSON.parse(root.querySelector('[data-frjs-config]').textContent);
      const chooser = root.querySelector('[data-frjs-chooser]');
      const form = root.querySelector('[data-frjs-form]');
      const resultBox = root.querySelector('[data-frjs-result]');
      const status = root.querySelector('[data-frjs-status]');
      const validation = root.querySelector('[data-frjs-validation]');
      const summaryDetails = root.querySelector('[data-frjs-summary-details]');
      const summary = root.querySelector('[data-frjs-summary]');
      const copyFeedback = root.querySelector('[data-frjs-copy-feedback]');
      const controls = data.chooser.questions.map((question) => form.elements.namedItem(question.id));
      if (controls.some((control) => !control) || !resultBox || !summary) return;

      function clearValidation() {
        validation.hidden = true;
        controls.forEach((control) => control.removeAttribute('aria-invalid'));
      }
      function openChooser() {
        chooser.open = true;
        controls[0].focus();
      }
      root.querySelectorAll('[data-frjs-open-chooser]').forEach((link) => {
        link.addEventListener('click', (event) => { event.preventDefault(); openChooser(); });
      });
      form.addEventListener('change', () => {
        clearValidation();
        if (!resultBox.hidden) {
          resultBox.hidden = true;
          status.textContent = 'Réponses modifiées. Cliquez sur « Voir mes repères » pour les actualiser.';
        }
      });
      form.addEventListener('submit', (event) => {
        event.preventDefault();
        clearValidation();
        const answers = Object.fromEntries(data.chooser.questions.map((question) => [question.id, form.elements.namedItem(question.id).value]));
        const optional = Object.fromEntries(data.chooser.optional_fields.map((field) => [field.id, form.elements.namedItem(field.id).value]));
        const result = calculateResult(data, answers, optional);
        if (!result.valid) {
          resultBox.hidden = true;
          validation.textContent = data.chooser.ui.validation;
          validation.hidden = false;
          result.missing.forEach((id) => form.elements.namedItem(id).setAttribute('aria-invalid', 'true'));
          if (result.missing.length) form.elements.namedItem(result.missing[0]).focus();
          return;
        }
        renderResult(root, data, result);
        resultBox.hidden = false;
        copyFeedback.textContent = '';
        status.textContent = 'Vos repères sont prêts.';
        root.querySelector('[data-frjs-result-title]').focus();
      });
      root.querySelector('[data-frjs-edit]').addEventListener('click', openChooser);
      root.querySelector('[data-frjs-restart]').addEventListener('click', () => {
        form.reset();
        clearValidation();
        resultBox.hidden = true;
        summary.value = '';
        copyFeedback.textContent = '';
        status.textContent = 'Les réponses ont été effacées.';
        openChooser();
      });
      root.querySelector('[data-frjs-copy]').addEventListener('click', async () => {
        try {
          const clipboard = document.defaultView?.navigator.clipboard;
          if (!clipboard?.writeText) throw new Error('Clipboard unavailable');
          await clipboard.writeText(summary.value);
          copyFeedback.textContent = data.chooser.ui.copy_success;
        } catch {
          summaryDetails.open = true;
          summary.focus();
          summary.select();
          copyFeedback.textContent = data.chooser.ui.copy_failure;
        }
      });
      root.dataset.frjsReady = 'true';
      chooser.hidden = false;
      root.querySelectorAll('[data-frjs-open-chooser]').forEach((link) => { link.hidden = false; });
      const releaseHash = bindChooserHash(root, chooser, controls[0], document.defaultView);
      const onSectionUnload = (event) => {
        if (event.target === root || event.target.contains?.(root)) {
          releaseHash();
          document.removeEventListener('shopify:section:unload', onSectionUnload);
        }
      };
      document.addEventListener('shopify:section:unload', onSectionUnload);
    } catch {
      // The ordinary guide links remain the usable fallback if enhancement fails.
    }
  });
}

if (typeof document !== 'undefined') {
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => initGuideHubs(document), { once: true });
  else initGuideHubs(document);
  document.addEventListener('shopify:section:load', (event) => initGuideHubs(event.target));
}
